import type { MailConversation, MailUnifiedConversation } from './api'

// Offline store for Ember Mail (#785): the INBOX list of every unprotected account
// (newest 50 conversations incl. bodies), the unified inbox (#535), attachments the
// user already opened, the metadata needed to render after an offline reload, and
// the outbox records (owned by mail-outbox.ts). One IndexedDB database per user.
//
// Customer data (#524) is enforced HERE, not only in the UI: every write checks the
// protection snapshot fail-closed (unknown account/folder or snapshot not loaded =
// protected), every read re-checks it and deletes what is no longer allowed, and
// every record carries the userId it belongs to (mismatch = missing, deleted).

export type OfflineStoreName = 'lists' | 'attachments' | 'outbox' | 'meta'

const STORE_NAMES: readonly OfflineStoreName[] = ['lists', 'attachments', 'outbox', 'meta']
const DB_VERSION = 1

export interface OfflineBackend {
  get<T>(store: OfflineStoreName, key: string): Promise<T | undefined>
  put(store: OfflineStoreName, key: string, value: unknown): Promise<void>
  delete(store: OfflineStoreName, key: string): Promise<void>
  entries<T>(store: OfflineStoreName): Promise<Array<{ key: string; value: T }>>
  close(): void
  /** true once closed (by close(), a deleteDatabase or another tab's version change). */
  readonly closed?: boolean
}

export interface OfflineBackendFactory {
  open(dbName: string): Promise<OfflineBackend>
  deleteDatabase(dbName: string): Promise<void>
  /** null = the browser cannot enumerate databases. */
  databases(): Promise<string[] | null>
}

export const OFFLINE_DB_PREFIX = 'ember-mail-offline:v1:'
export const OFFLINE_LIST_LIMIT = 50
export const OFFLINE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
export const ATTACHMENTS_TOTAL_MAX_BYTES = 50 * 1024 * 1024

export function offlineDbName(userId: string): string {
  return OFFLINE_DB_PREFIX + userId
}

function closedError(): Error {
  return new Error('Offline-Speicher ist geschlossen')
}

// ---------------------------------------------------------------------------
// IndexedDB backend (browser)

// Open connections per database name, shared by every factory instance of this
// module, so deleteDatabase can close them first instead of being blocked by us.
const openConnections = new Map<string, Set<OfflineBackend>>()

function trackConnection(name: string, backend: OfflineBackend): void {
  let set = openConnections.get(name)
  if (!set) {
    set = new Set()
    openConnections.set(name, set)
  }
  set.add(backend)
}

function untrackConnection(name: string, backend: OfflineBackend): void {
  const set = openConnections.get(name)
  if (!set) return
  set.delete(backend)
  if (set.size === 0) openConnections.delete(name)
}

function defaultIdb(): IDBFactory | undefined {
  try {
    return typeof indexedDB === 'undefined' ? undefined : indexedDB
  } catch {
    return undefined
  }
}

function idbBackend(name: string, db: IDBDatabase): OfflineBackend {
  let closed = false
  const markClosed = () => {
    closed = true
    untrackConnection(name, backend)
  }
  // Another tab (or our own factory) deletes the database: let it through.
  db.onversionchange = () => {
    db.close()
    markClosed()
  }
  // The browser closed the connection unexpectedly (storage cleared, …).
  db.onclose = markClosed

  function run<T>(
    store: OfflineStoreName,
    mode: IDBTransactionMode,
    body: (s: IDBObjectStore, done: (value: T) => void) => void,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (closed) {
        reject(closedError())
        return
      }
      let result: T | undefined
      let tx: IDBTransaction
      try {
        tx = db.transaction(store, mode)
        body(tx.objectStore(store), (value) => {
          result = value
        })
      } catch (err) {
        reject(err)
        return
      }
      tx.oncomplete = () => resolve(result as T)
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB-Fehler'))
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB-Transaktion abgebrochen'))
    })
  }

  const backend: OfflineBackend = {
    get closed() {
      return closed
    },
    get<T>(store: OfflineStoreName, key: string) {
      return run<T | undefined>(store, 'readonly', (s, done) => {
        const req = s.get(key)
        req.onsuccess = () => done(req.result as T | undefined)
      })
    },
    put(store, key, value) {
      return run<void>(store, 'readwrite', (s) => {
        s.put(value, key)
      })
    },
    delete(store, key) {
      return run<void>(store, 'readwrite', (s) => {
        s.delete(key)
      })
    },
    entries<T>(store: OfflineStoreName) {
      return run<Array<{ key: string; value: T }>>(store, 'readonly', (s, done) => {
        const out: Array<{ key: string; value: T }> = []
        done(out)
        const req = s.openCursor()
        req.onsuccess = () => {
          const cursor = req.result
          if (!cursor) return
          out.push({ key: String(cursor.key), value: cursor.value as T })
          cursor.continue()
        }
      })
    },
    close() {
      if (closed) return
      db.close()
      markClosed()
    },
  }
  trackConnection(name, backend)
  return backend
}

/** Real browser backend: one database per name, version 1, the four object stores
 * created in onupgradeneeded, each operation in its own transaction. */
export function indexedDbFactory(idb?: IDBFactory): OfflineBackendFactory {
  const factory = () => idb ?? defaultIdb()
  return {
    open(dbName) {
      const f = factory()
      if (!f) return Promise.reject(new Error('IndexedDB ist nicht verfügbar'))
      return new Promise<OfflineBackend>((resolve, reject) => {
        let req: IDBOpenDBRequest
        try {
          req = f.open(dbName, DB_VERSION)
        } catch (err) {
          reject(err)
          return
        }
        req.onupgradeneeded = () => {
          const db = req.result
          for (const s of STORE_NAMES) {
            if (!db.objectStoreNames.contains(s)) db.createObjectStore(s)
          }
        }
        req.onsuccess = () => resolve(idbBackend(dbName, req.result))
        req.onerror = () => reject(req.error ?? new Error('IndexedDB konnte nicht geöffnet werden'))
      })
    },
    deleteDatabase(dbName) {
      const f = factory()
      if (!f) return Promise.resolve()
      // Close our own connections first; other tabs get `versionchange` and close.
      for (const backend of [...(openConnections.get(dbName) ?? [])]) backend.close()
      return new Promise<void>((resolve, reject) => {
        let req: IDBOpenDBRequest
        try {
          req = f.deleteDatabase(dbName)
        } catch (err) {
          reject(err)
          return
        }
        req.onsuccess = () => resolve()
        // Still open somewhere else: the deletion is queued and runs as soon as that
        // connection closes; nothing can read the data in between (new opens wait).
        req.onblocked = () => resolve()
        req.onerror = () => reject(req.error ?? new Error('IndexedDB konnte nicht gelöscht werden'))
      })
    },
    async databases() {
      const f = factory()
      if (!f || typeof f.databases !== 'function') return null
      try {
        const list = await f.databases()
        return list.map((d) => d.name).filter((n): n is string => typeof n === 'string')
      } catch {
        return null
      }
    },
  }
}

// ---------------------------------------------------------------------------
// In-memory backend (tests; same semantics)

/** structuredClone, but Blob values are kept as-is (like IndexedDB keeps a handle). */
function cloneValue<T>(value: T): T {
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value
  if (Array.isArray(value)) return value.map((v) => cloneValue(v)) as T
  if (value !== null && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value)
    if (proto === Object.prototype || proto === null) {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value)) out[k] = cloneValue(v)
      return out as T
    }
  }
  return structuredClone(value)
}

export function memoryFactory(): OfflineBackendFactory {
  const dbs = new Map<string, Map<OfflineStoreName, Map<string, unknown>>>()
  const conns = new Map<string, Set<{ closed: boolean }>>()

  function ensure(name: string): Map<OfflineStoreName, Map<string, unknown>> {
    let db = dbs.get(name)
    if (!db) {
      db = new Map(STORE_NAMES.map((s) => [s, new Map<string, unknown>()]))
      dbs.set(name, db)
    }
    return db
  }

  return {
    async open(dbName) {
      const db = ensure(dbName)
      const conn = { closed: false }
      let set = conns.get(dbName)
      if (!set) {
        set = new Set()
        conns.set(dbName, set)
      }
      set.add(conn)
      const table = async (store: OfflineStoreName): Promise<Map<string, unknown>> => {
        await Promise.resolve()
        if (conn.closed) throw closedError()
        const t = db.get(store)
        if (!t) throw new Error('Unbekannter Speicher')
        return t
      }
      return {
        get closed() {
          return conn.closed
        },
        async get<T>(store: OfflineStoreName, key: string) {
          const t = await table(store)
          return t.has(key) ? cloneValue(t.get(key) as T) : undefined
        },
        async put(store, key, value) {
          ;(await table(store)).set(key, cloneValue(value))
        },
        async delete(store, key) {
          ;(await table(store)).delete(key)
        },
        async entries<T>(store: OfflineStoreName) {
          const t = await table(store)
          return [...t.entries()]
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([key, value]) => ({ key, value: cloneValue(value as T) }))
        },
        close() {
          conn.closed = true
          conns.get(dbName)?.delete(conn)
        },
      }
    },
    async deleteDatabase(dbName) {
      for (const conn of conns.get(dbName) ?? []) conn.closed = true
      conns.delete(dbName)
      dbs.delete(dbName)
    },
    async databases() {
      return [...dbs.keys()]
    },
  }
}

// ---------------------------------------------------------------------------
// Protection (fail-closed)

export interface ProtectionSnapshot {
  accounts: ReadonlyArray<{ id: string; protected: boolean }>
  folders: ReadonlyArray<{ accountId: string; path: string; protected: boolean }>
}

/** true only if the account is known with protected === false AND (the folder is
 * known with protected === false, OR the folder is unknown and serverFolderProtected
 * === false). A known protected folder always wins. No snapshot → false. */
export function isStorable(
  snapshot: ProtectionSnapshot | null | undefined,
  accountId: string,
  folderPath: string,
  serverFolderProtected?: boolean,
): boolean {
  if (!snapshot) return false
  const account = snapshot.accounts.find((a) => a.id === accountId)
  if (!account || account.protected !== false) return false
  const folder = snapshot.folders.find((f) => f.accountId === accountId && f.path === folderPath)
  if (folder) return folder.protected === false
  return serverFolderProtected === false
}

// ---------------------------------------------------------------------------
// Store

export interface OfflineList<C> {
  conversations: C[]
  savedAt: number
}

export interface OfflineAttachmentRef {
  accountId: string
  folderPath: string
  messageId: string
  attachmentId: string
}

/** Name and type of a kept attachment: after an offline reload the server detail
 * is gone, so this is what the mail view shows the stored file by. */
export interface OfflineAttachmentInfo {
  filename: string
  contentType: string
}

export interface OfflineAttachmentListed extends OfflineAttachmentInfo {
  attachmentId: string
  size: number
}

export interface OfflineMetaAccount {
  id: string
  label: string
  email: string
  protected: boolean
  disabledAt: string | null
}

export interface OfflineMetaFolder {
  accountId: string
  path: string
  name: string
  protected: boolean
  /** When the row was last seen in server data (rows carried over expire). */
  savedAt?: number
}

export interface OfflineMeta {
  me: unknown
  accounts: OfflineMetaAccount[]
  folders: OfflineMetaFolder[]
  savedAt: number
}

const INBOX = 'INBOX'
const UNIFIED_KEY = 'unified'
const META_KEY = 'meta'

interface ListRecord {
  kind: 'list'
  userId: string
  accountId: string
  folderPath: string
  savedAt: number
  conversations: MailConversation[]
}

interface UnifiedRecord {
  kind: 'unified'
  userId: string
  savedAt: number
  conversations: MailUnifiedConversation[]
}

interface AttachmentRecord {
  userId: string
  accountId: string
  folderPath: string
  messageId: string
  attachmentId: string
  size: number
  savedAt: number
  blob: Blob
  /** Absent in records written before the name was kept (then never listed). */
  filename?: string
  contentType?: string
}

interface MetaRecord extends OfflineMeta {
  userId: string
}

type Stamped = { userId?: unknown; savedAt?: unknown }

function listKey(accountId: string, folderPath: string): string {
  return `list:${JSON.stringify([accountId, folderPath])}`
}

function attachmentKey(ref: { accountId: string; messageId: string; attachmentId: string }) {
  return `att:${JSON.stringify([ref.accountId, ref.messageId, ref.attachmentId])}`
}

function sortTime(c: MailConversation): number {
  const t = c.latest?.internalDate ? Date.parse(c.latest.internalDate) : Number.NaN
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t
}

function newestFirst<C extends MailConversation>(list: C[]): C[] {
  return [...list].sort((a, b) => sortTime(b) - sortTime(a)).slice(0, OFFLINE_LIST_LIMIT)
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object'
}

function unifiedStorable(
  snapshot: ProtectionSnapshot | null | undefined,
  c: MailUnifiedConversation,
): boolean {
  return c.protected === false && isStorable(snapshot, c.accountId, INBOX, c.protected)
}

export class MailOfflineStore {
  readonly userId: string
  private readonly backend: OfflineBackend
  private readonly now: () => number
  private closedFlag = false

  private constructor(userId: string, backend: OfflineBackend, now: () => number) {
    this.userId = userId
    this.backend = backend
    this.now = now
  }

  static async open(opts: {
    userId: string
    factory: OfflineBackendFactory
    now?: () => number
  }): Promise<MailOfflineStore> {
    if (!opts.userId) throw new Error('Offline-Speicher braucht einen Nutzer')
    const backend = await opts.factory.open(offlineDbName(opts.userId))
    return new MailOfflineStore(opts.userId, backend, opts.now ?? Date.now)
  }

  /** true once closed or once the database was deleted underneath (other tab, purge). */
  get closed(): boolean {
    return this.closedFlag || this.backend.closed === true
  }

  /** Own, not expired record → it; foreign userId or expired → deleted, undefined. */
  private async readOwn<T extends Stamped>(
    store: OfflineStoreName,
    key: string,
  ): Promise<T | undefined> {
    const rec = await this.backend.get<T>(store, key)
    if (rec === undefined) return undefined
    if (!this.isValid(rec)) {
      await this.backend.delete(store, key)
      return undefined
    }
    return rec
  }

  private isValid(rec: unknown): rec is Stamped & { savedAt: number } {
    if (!isObject(rec) || rec.userId !== this.userId) return false
    if (typeof rec.savedAt !== 'number') return false
    return this.now() - rec.savedAt <= OFFLINE_MAX_AGE_MS
  }

  // --- INBOX list of one account --------------------------------------------

  async saveList(
    accountId: string,
    folderPath: string,
    conversations: MailConversation[],
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<number> {
    if (folderPath !== INBOX) return 0
    const key = listKey(accountId, folderPath)
    if (!isStorable(snapshot, accountId, folderPath)) {
      // Protected (or unknown) now: whatever an earlier save left goes away.
      await this.backend.delete('lists', key)
      return 0
    }
    const own = conversations.filter((c) => c.latest?.accountId === accountId)
    const kept = newestFirst(own)
    const rec: ListRecord = {
      kind: 'list',
      userId: this.userId,
      accountId,
      folderPath,
      savedAt: this.now(),
      conversations: kept,
    }
    await this.backend.put('lists', key, rec)
    return kept.length
  }

  async readList(
    accountId: string,
    folderPath: string,
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<OfflineList<MailConversation> | null> {
    if (folderPath !== INBOX) return null
    const key = listKey(accountId, folderPath)
    const rec = await this.readOwn<ListRecord>('lists', key)
    if (!rec) return null
    if (!isStorable(snapshot, accountId, folderPath) || !Array.isArray(rec.conversations)) {
      await this.backend.delete('lists', key)
      return null
    }
    return { conversations: rec.conversations, savedAt: rec.savedAt }
  }

  // --- unified inbox (#535) ----------------------------------------------------

  async saveUnified(
    conversations: MailUnifiedConversation[],
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<number> {
    const kept = newestFirst(conversations.filter((c) => unifiedStorable(snapshot, c)))
    if (kept.length === 0) {
      await this.backend.delete('lists', UNIFIED_KEY)
      return 0
    }
    const rec: UnifiedRecord = {
      kind: 'unified',
      userId: this.userId,
      savedAt: this.now(),
      conversations: kept,
    }
    await this.backend.put('lists', UNIFIED_KEY, rec)
    return kept.length
  }

  async readUnified(
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<OfflineList<MailUnifiedConversation> | null> {
    const rec = await this.readOwn<UnifiedRecord>('lists', UNIFIED_KEY)
    if (!rec) return null
    const all = Array.isArray(rec.conversations) ? rec.conversations : []
    const kept = all.filter((c) => unifiedStorable(snapshot, c))
    if (kept.length !== all.length) await this.writeUnified(rec, kept)
    if (kept.length === 0) return null
    return { conversations: kept, savedAt: rec.savedAt }
  }

  /** Rewrites the unified record with fewer conversations (never refreshes savedAt). */
  private async writeUnified(
    rec: UnifiedRecord,
    conversations: MailUnifiedConversation[],
  ): Promise<void> {
    if (conversations.length === 0) await this.backend.delete('lists', UNIFIED_KEY)
    else await this.backend.put('lists', UNIFIED_KEY, { ...rec, conversations })
  }

  // --- attachments -------------------------------------------------------------

  async saveAttachment(
    ref: OfflineAttachmentRef,
    blob: Blob,
    snapshot: ProtectionSnapshot | null | undefined,
    info?: OfflineAttachmentInfo,
  ): Promise<boolean> {
    if (!isStorable(snapshot, ref.accountId, ref.folderPath)) return false
    if (blob.size > ATTACHMENT_MAX_BYTES) return false
    const key = attachmentKey(ref)
    const rec: AttachmentRecord = {
      userId: this.userId,
      accountId: ref.accountId,
      folderPath: ref.folderPath,
      messageId: ref.messageId,
      attachmentId: ref.attachmentId,
      size: blob.size,
      savedAt: this.now(),
      blob,
      ...(info ? { filename: info.filename, contentType: info.contentType } : {}),
    }
    await this.backend.put('attachments', key, rec)
    await this.evictAttachments(key)
    return true
  }

  /** Oldest first out until the total fits ATTACHMENTS_TOTAL_MAX_BYTES (keeps `keep`). */
  private async evictAttachments(keep: string): Promise<void> {
    const own: Array<{ key: string; size: number; savedAt: number }> = []
    for (const e of await this.backend.entries<AttachmentRecord>('attachments')) {
      if (!this.isValid(e.value)) {
        await this.backend.delete('attachments', e.key)
        continue
      }
      own.push({ key: e.key, size: Number(e.value.size) || 0, savedAt: e.value.savedAt })
    }
    let total = own.reduce((sum, e) => sum + e.size, 0)
    own.sort((a, b) => a.savedAt - b.savedAt)
    for (const e of own) {
      if (total <= ATTACHMENTS_TOTAL_MAX_BYTES) break
      if (e.key === keep) continue
      await this.backend.delete('attachments', e.key)
      total -= e.size
    }
  }

  async readAttachment(
    ref: OfflineAttachmentRef,
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<Blob | null> {
    const key = attachmentKey(ref)
    const rec = await this.readOwn<AttachmentRecord>('attachments', key)
    if (!rec) return null
    if (
      !isStorable(snapshot, rec.accountId, rec.folderPath) ||
      !isStorable(snapshot, ref.accountId, ref.folderPath)
    ) {
      await this.backend.delete('attachments', key)
      return null
    }
    return rec.blob instanceof Blob ? rec.blob : null
  }

  /** The kept attachments of one mail (after an offline reload the server detail is
   * missing). Fail-closed like readAttachment: nothing unless the snapshot vouches
   * for the account and folder; expired and foreign records are dropped. */
  async listAttachments(
    mail: { accountId: string; folderPath: string; messageId: string },
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<OfflineAttachmentListed[]> {
    if (!isStorable(snapshot, mail.accountId, mail.folderPath)) return []
    const out: OfflineAttachmentListed[] = []
    for (const e of await this.backend.entries<AttachmentRecord>('attachments')) {
      const rec = e.value
      if (!this.isValid(rec)) {
        await this.backend.delete('attachments', e.key)
        continue
      }
      if (rec.accountId !== mail.accountId || rec.messageId !== mail.messageId) continue
      if (!isStorable(snapshot, rec.accountId, rec.folderPath)) continue
      if (typeof rec.filename !== 'string' || !(rec.blob instanceof Blob)) continue
      out.push({
        attachmentId: rec.attachmentId,
        filename: rec.filename,
        contentType:
          typeof rec.contentType === 'string' ? rec.contentType : 'application/octet-stream',
        size: Number(rec.size) || 0,
      })
    }
    return out
  }

  async hasAttachment(ref: {
    accountId: string
    messageId: string
    attachmentId: string
  }): Promise<boolean> {
    return (await this.readOwn<AttachmentRecord>('attachments', attachmentKey(ref))) !== undefined
  }

  // --- metadata for an offline reload -----------------------------------------

  async saveMeta(meta: {
    me: unknown
    accounts: ReadonlyArray<OfflineMetaAccount>
    folders?: ReadonlyArray<OfflineMetaFolder>
  }): Promise<void> {
    // Whitelisted fields only; protected accounts, protected folders and folders of
    // protected or unknown accounts are dropped before anything is written.
    const accounts: OfflineMetaAccount[] = meta.accounts
      .filter((a) => a.protected === false)
      .map((a) => ({
        id: a.id,
        label: a.label,
        email: a.email,
        protected: false,
        disabledAt: a.disabledAt ?? null,
      }))
    const allowed = new Set(accounts.map((a) => a.id))
    const folders: OfflineMetaFolder[] = (meta.folders ?? [])
      .filter((f) => f.protected === false && allowed.has(f.accountId))
      .map((f) => ({
        accountId: f.accountId,
        path: f.path,
        name: f.name,
        protected: false,
        ...(typeof f.savedAt === 'number' ? { savedAt: f.savedAt } : {}),
      }))
    const rec: MetaRecord = {
      userId: this.userId,
      savedAt: this.now(),
      me: meta.me,
      accounts,
      folders,
    }
    await this.backend.put('meta', META_KEY, rec)
  }

  async readMeta(): Promise<OfflineMeta | null> {
    const rec = await this.readOwn<MetaRecord>('meta', META_KEY)
    if (!rec) return null
    return {
      me: rec.me,
      accounts: Array.isArray(rec.accounts) ? rec.accounts : [],
      folders: Array.isArray(rec.folders) ? rec.folders : [],
      savedAt: rec.savedAt,
    }
  }

  // --- purge ------------------------------------------------------------------

  /** Lists, unified entries, attachments and meta rows of that account (the outbox
   * is handled by mail-outbox.ts: waiting mails become server drafts). */
  async purgeAccount(accountId: string): Promise<void> {
    await this.purgeWhere(accountId, null)
  }

  async purgeFolder(accountId: string, folderPath: string): Promise<void> {
    await this.purgeWhere(accountId, folderPath)
  }

  private async purgeWhere(accountId: string, folderPath: string | null): Promise<void> {
    const hit = (acc: unknown, path: unknown) =>
      acc === accountId && (folderPath === null || path === folderPath)

    for (const e of await this.backend.entries<ListRecord | UnifiedRecord>('lists')) {
      const v = e.value
      if (!isObject(v)) continue
      if (e.key === UNIFIED_KEY) {
        if (folderPath !== null && folderPath !== INBOX) continue
        const rec = v as UnifiedRecord
        const all = Array.isArray(rec.conversations) ? rec.conversations : []
        const kept = all.filter((c) => c.accountId !== accountId)
        if (kept.length !== all.length) await this.writeUnified(rec, kept)
      } else if (hit(v.accountId, v.folderPath)) {
        await this.backend.delete('lists', e.key)
      }
    }
    for (const e of await this.backend.entries<AttachmentRecord>('attachments')) {
      if (isObject(e.value) && hit(e.value.accountId, e.value.folderPath)) {
        await this.backend.delete('attachments', e.key)
      }
    }
    const meta = await this.backend.get<MetaRecord>('meta', META_KEY)
    if (isObject(meta)) {
      const accounts = Array.isArray(meta.accounts) ? meta.accounts : []
      const folders = Array.isArray(meta.folders) ? meta.folders : []
      const nextAccounts =
        folderPath === null ? accounts.filter((a) => a.id !== accountId) : accounts
      const nextFolders = folders.filter((f) => !hit(f.accountId, f.path))
      if (nextAccounts.length !== accounts.length || nextFolders.length !== folders.length) {
        await this.backend.put('meta', META_KEY, {
          ...meta,
          accounts: nextAccounts,
          folders: nextFolders,
        })
      }
    }
  }

  /** Drops lists, unified entries, attachments and meta older than OFFLINE_MAX_AGE_MS
   * (and records of another user). Never touches the outbox. */
  async pruneExpired(): Promise<void> {
    for (const store of ['lists', 'attachments', 'meta'] as const) {
      for (const e of await this.backend.entries<unknown>(store)) {
        if (!this.isValid(e.value)) await this.backend.delete(store, e.key)
      }
    }
  }

  close(): void {
    this.closedFlag = true
    this.backend.close()
  }
}

export function purgeUser(factory: OfflineBackendFactory, userId: string): Promise<void> {
  return factory.deleteDatabase(offlineDbName(userId))
}

/** Deletes every offline database of another user (no-op when the browser cannot
 * enumerate databases). Databases without our prefix are never touched. */
export async function purgeForeignUsers(
  factory: OfflineBackendFactory,
  currentUserId: string,
): Promise<void> {
  const names = await factory.databases()
  if (!names) return
  const own = offlineDbName(currentUserId)
  for (const name of names) {
    if (name.startsWith(OFFLINE_DB_PREFIX) && name !== own) await factory.deleteDatabase(name)
  }
}

// ---------------------------------------------------------------------------
// Purged users: after logout/user switch/401 nothing may recreate their database
// (a late list answer in another tab, an outbox call) until they are current
// again (rememberOfflineUser).

const purgedUsers = new Set<string>()

/** True between a purge of this user and the next rememberOfflineUser(userId). */
export function isOfflinePurged(userId: string): boolean {
  return purgedUsers.has(userId)
}

/** Error of getMailOfflineStore / the outbox for a purged user. */
export function offlinePurgedError(): Error {
  return Object.assign(new Error('Offline-Speicher wurde geleert'), { code: 'OFFLINE_PURGED' })
}

// ---------------------------------------------------------------------------
// Last-user pointer for an offline boot (only the userId, never content)

const LAST_USER_KEY = 'ember-mail-offline:last-user'

function browserStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

export function rememberOfflineUser(userId: string, storage?: Pick<Storage, 'setItem'>): void {
  // The user is current again (login, mail page mounted): storing is allowed.
  purgedUsers.delete(userId)
  try {
    ;(storage ?? browserStorage())?.setItem(LAST_USER_KEY, userId)
  } catch {
    // storage blocked: offline boot simply is not available
  }
}

export function lastOfflineUser(storage?: Pick<Storage, 'getItem'>): string | null {
  try {
    return (storage ?? browserStorage())?.getItem(LAST_USER_KEY) || null
  } catch {
    return null
  }
}

export function forgetOfflineUser(storage?: Pick<Storage, 'removeItem'>): void {
  try {
    ;(storage ?? browserStorage())?.removeItem(LAST_USER_KEY)
  } catch {
    // storage blocked
  }
}

// ---------------------------------------------------------------------------
// App wiring

let appFactory: OfflineBackendFactory | null = null

/** The one IndexedDB factory the app uses (store and outbox share it). */
export function mailOfflineFactory(): OfflineBackendFactory {
  if (!appFactory) appFactory = indexedDbFactory()
  return appFactory
}

const storeCache = new Map<string, Promise<MailOfflineStore>>()

/** Cached store of this user; expired entries are pruned when it is first opened.
 * A store closed underneath (purge, other tab deleted the database) is reopened. */
export async function getMailOfflineStore(
  userId: string,
  factory: OfflineBackendFactory = mailOfflineFactory(),
): Promise<MailOfflineStore> {
  // Never recreate the database of a user whose data was just purged.
  if (purgedUsers.has(userId)) throw offlinePurgedError()
  const cached = storeCache.get(userId)
  if (cached) {
    try {
      const store = await cached
      if (!store.closed) return store
    } catch {
      // failed open: try again below
    }
    if (storeCache.get(userId) === cached) storeCache.delete(userId)
    return getMailOfflineStore(userId, factory)
  }
  const opening = MailOfflineStore.open({ userId, factory }).then(async (store) => {
    try {
      await store.pruneExpired()
    } catch {
      // best effort: reads expire on their own
    }
    return store
  })
  storeCache.set(userId, opening)
  opening.catch(() => {
    if (storeCache.get(userId) === opening) storeCache.delete(userId)
  })
  return opening
}

/** Logout, user switch, 401: closes the cached store, forgets the offline-boot
 * pointer of this user and deletes the whole database (store + outbox). Until the
 * user is remembered again, nothing reopens (and thereby recreates) it. */
export async function purgeMailOfflineForUser(
  userId: string,
  factory: OfflineBackendFactory = mailOfflineFactory(),
): Promise<void> {
  purgedUsers.add(userId)
  // Pointer first, before any await (#834): an offline boot running meanwhile
  // must not find this user and restore the view.
  const last = lastOfflineUser()
  if (last === null || last === userId) forgetOfflineUser()
  const cached = storeCache.get(userId)
  storeCache.delete(userId)
  if (cached) {
    try {
      ;(await cached).close()
    } catch {
      // never opened
    }
  }
  await purgeUser(factory, userId)
}
